import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { LogOut, BarChart3, Package, FileText, MousePointerClick, Cable, Download, FolderKanban, ArrowRightLeft, FileCode, Mail, Megaphone } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, VENDOR_LABELS } from "@/lib/api";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import ArticlesPanel from "@/components/admin/ArticlesPanel";
import RedirectsPanel from "@/components/admin/RedirectsPanel";
import ProductsPanel from "@/components/admin/ProductsPanel";
import PagesPanel from "@/components/admin/PagesPanel";
import MessagesPanel from "@/components/admin/MessagesPanel";
import UrgencyPanel from "@/components/admin/UrgencyPanel";
import ClickInsights from "@/components/admin/ClickInsights";
import PriceRefreshPanel from "@/components/admin/PriceRefreshPanel";
import ImageUploader from "@/components/ImageUploader";

const TABS = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "products", label: "Products", icon: Package },
  { id: "categories", label: "Categories", icon: FolderKanban },
  { id: "articles", label: "Articles", icon: FileText },
  { id: "pages", label: "Pages", icon: FileCode },
  { id: "messages", label: "Messages", icon: Mail },
  { id: "clicks", label: "Clicks", icon: MousePointerClick },
  { id: "connectors", label: "Connectors", icon: Cable },
  { id: "redirects", label: "Redirects", icon: ArrowRightLeft },
  { id: "banner", label: "Banner", icon: Megaphone },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const t = searchParams.get("tab");
    return TABS.some((x) => x.id === t) ? t : "overview";
  });
  // `initialSlug` is consumed once by the active panel to deep-link from
  // a public page's "Edit on site" link. We clear it from the URL after
  // the panel mounts so re-renders don't keep re-selecting the record.
  const [initialSlug, setInitialSlug] = useState(() => searchParams.get("slug") || "");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [clicks, setClicks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [topRedirects, setTopRedirects] = useState([]);

  useEffect(() => {
    if (user === false) nav("/admin/login", { replace: true });
  }, [user, nav]);

  // Clean ?tab=…&slug=… out of the URL once we've captured them into state.
  // Without this, sharing or reloading the URL would keep re-deep-linking.
  useEffect(() => {
    if (searchParams.get("tab") || searchParams.get("slug")) {
      setSearchParams({}, { replace: true });
    }
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user || user === false) return;
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/products", { params: { page_size: 100 } }).then((r) => setProducts(r.data.items));
    api.get("/articles").then((r) => setArticles(r.data));
    api.get("/admin/clicks", { params: { limit: 100 } }).then((r) => setClicks(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/redirects")
      .then((r) => {
        const sorted = [...r.data]
          .filter((x) => (x.hits || 0) > 0)
          .sort((a, b) => (b.hits || 0) - (a.hits || 0))
          .slice(0, 8);
        setTopRedirects(sorted);
      })
      .catch(() => setTopRedirects([]));
  }, [user]);

  if (!user || user === false) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 font-mono uppercase text-xs tracking-widest opacity-70">
        Loading...
      </div>
    );
  }

  const onLogout = async () => {
    await logout();
    toast.success("Signed out");
    nav("/admin/login");
  };

  return (
    <div data-testid="admin-dashboard">
      <Seo title="Admin Dashboard" noindex />
      <section className="border-b-2 border-black">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 flex flex-wrap items-center gap-4">
          <div>
            <div className="font-mono uppercase text-[11px] tracking-widest opacity-70">
              Logged in as
            </div>
            <div className="font-display text-2xl md:text-3xl tracking-tight">
              {user.email}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Link
              to="/"
              className="brut bg-white px-3 py-2 font-mono uppercase text-[11px] tracking-widest brut-press"
            >
              View site
            </Link>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="brut pcs-bg-pink text-white px-3 py-2 font-mono uppercase text-[11px] tracking-widest brut-press inline-flex items-center gap-2"
            >
              <LogOut className="w-3.5 h-3.5" strokeWidth={2.5} />
              Sign out
            </button>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 pb-4 flex flex-wrap gap-2">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => { setTab(t.id); setInitialSlug(""); }}
                data-testid={`admin-tab-${t.id}`}
                className={`brut px-3 py-2 font-mono uppercase text-[11px] tracking-widest brut-press inline-flex items-center gap-2 ${
                  tab === t.id ? "pcs-bg-blue text-white" : "bg-white"
                }`}
              >
                <Icon className="w-3.5 h-3.5" strokeWidth={2.5} />
                {t.label}
              </button>
            );
          })}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {tab === "overview" && stats && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat label="Products" value={stats.products} accent="pink" />
              <Stat label="Articles" value={stats.articles} accent="blue" />
              <Stat label="Categories" value={stats.categories} accent="orange" />
              <Stat label="Total clicks" value={stats.total_clicks} accent="pink" />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="brut bg-white p-5">
                <div className="font-mono uppercase text-[11px] tracking-widest opacity-70 mb-3">
                  Clicks by vendor
                </div>
                {Object.keys(stats.clicks_by_vendor).length === 0 ? (
                  <div className="text-sm text-gray-700">
                    No clicks yet — open a product page and tap "Shop on..." to
                    seed some.
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {Object.entries(stats.clicks_by_vendor).map(([k, v]) => (
                      <li key={k} className="flex items-center justify-between border-b-2 border-dashed border-black/30 pb-2">
                        <span className="font-bold">{VENDOR_LABELS[k] || k}</span>
                        <span className="font-display text-2xl">{v}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="brut bg-white p-5">
                <div className="font-mono uppercase text-[11px] tracking-widest opacity-70 mb-3">
                  Top products
                </div>
                {stats.top_products.length === 0 ? (
                  <div className="text-sm text-gray-700">No data yet.</div>
                ) : (
                  <ul className="space-y-2">
                    {stats.top_products.slice(0, 5).map((p) => (
                      <li key={p.slug} className="flex items-center justify-between border-b-2 border-dashed border-black/30 pb-2">
                        <Link to={`/product/${p.slug}`} className="font-bold hover:pcs-text-pink truncate mr-3">
                          {p.title}
                        </Link>
                        <span className="font-display text-xl shrink-0">
                          {p.click_count}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Most-hit redirects — surfaces which legacy URLs still pull
                real traffic so the admin can prioritise content refreshes. */}
            <div className="brut bg-white p-5" data-testid="overview-top-redirects">
              <div className="flex items-center mb-3">
                <div className="font-mono uppercase text-[11px] tracking-widest opacity-70">
                  Most-hit redirects
                </div>
                <button
                  type="button"
                  onClick={() => setTab("redirects")}
                  data-testid="overview-redirects-jump"
                  className="ml-auto font-mono uppercase text-[10px] tracking-widest underline hover:pcs-text-pink"
                >
                  Manage →
                </button>
              </div>
              {topRedirects.length === 0 ? (
                <div className="text-sm text-gray-700">
                  No redirect hits yet. Add some legacy → new URL rules in the{" "}
                  <button
                    type="button"
                    onClick={() => setTab("redirects")}
                    className="underline hover:pcs-text-pink"
                  >
                    Redirects tab
                  </button>
                  .
                </div>
              ) : (
                <>
                  <p className="text-xs opacity-70 mb-3">
                    These legacy URLs are still pulling traffic. They're the highest-leverage spots to publish a fresh article that links to current products.
                  </p>
                  <ul className="space-y-2">
                    {topRedirects.map((r) => (
                      <li
                        key={r.id}
                        className="grid grid-cols-[1fr_auto] items-center gap-2 border-b-2 border-dashed border-black/30 pb-2"
                        data-testid={`overview-redirect-${r.id}`}
                      >
                        <div className="min-w-0">
                          <code className="block font-mono text-xs truncate" title={r.from_path}>
                            {r.from_path}
                          </code>
                          <code className="block font-mono text-[11px] truncate pcs-text-pink" title={r.to_path}>
                            → {r.to_path}
                          </code>
                        </div>
                        <span className="font-display text-2xl shrink-0">
                          {r.hits}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        )}

        {tab === "products" && (
          <div className="space-y-4">
            <ManualAddProductPanel
              onCreated={(p) => setProducts((prev) => [p, ...prev])}
            />
            <ProductsPanel
              products={products}
              setProducts={setProducts}
              categories={categories}
              initialSlug={initialSlug}
            />
          </div>
        )}

        {tab === "pages" && <PagesPanel initialSlug={initialSlug} />}

        {tab === "messages" && <MessagesPanel />}

        {tab === "categories" && <CategoriesPanel initialSlug={initialSlug} />}

        {tab === "articles" && <ArticlesPanel initialSlug={initialSlug} />}

        {tab === "redirects" && <RedirectsPanel />}

        {tab === "banner" && <UrgencyPanel onOpenArticle={(slug) => { setInitialSlug(slug); setTab("articles"); }} />}

        {tab === "clicks" && (
          <div className="space-y-6">
          <ClickInsights />
          <div className="brut bg-white overflow-x-auto">
            {clicks.length === 0 ? (
              <div className="p-6 text-sm text-gray-700">No clicks tracked yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="font-mono uppercase text-[10px] tracking-widest pcs-bg-orange">
                  <tr>
                    <th className="text-left p-3 border-b-2 border-black">When</th>
                    <th className="text-left p-3 border-b-2 border-black">Product</th>
                    <th className="text-left p-3 border-b-2 border-black">Vendor</th>
                    <th className="text-left p-3 border-b-2 border-black">Button</th>
                    <th className="text-left p-3 border-b-2 border-black">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {clicks.map((c) => (
                    <tr key={c.id} className="border-b border-black/20">
                      <td className="p-3 font-mono text-[11px]">
                        {new Date(c.ts).toLocaleString()}
                      </td>
                      <td className="p-3">
                        <Link to={`/product/${c.product_slug}`} className="font-bold hover:pcs-text-pink">
                          {c.product_slug}
                        </Link>
                      </td>
                      <td className="p-3 font-mono uppercase text-[11px]">
                        {VENDOR_LABELS[c.vendor] || c.vendor}
                      </td>
                      <td className="p-3 font-mono uppercase text-[11px]" data-testid={`click-source-${c.id}`}>
                        {{ main: "Main", look: "Look" }[c.source] || "Other"}
                      </td>
                      <td className="p-3 font-mono text-[11px] opacity-70">{c.ip}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          </div>
        )}

        {tab === "connectors" && stats && (
          <div className="space-y-6">
            <PriceRefreshPanel />
            <div className="grid md:grid-cols-3 gap-4">
              {stats.connectors.map((c) => (
                <div
                  key={c.name}
                  className="brut bg-white p-5"
                  data-testid={`connector-${c.name}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-display text-2xl tracking-tight">
                      {c.vendor_label}
                    </div>
                    <span
                      className={`brut px-2 py-0.5 font-mono uppercase text-[10px] tracking-widest ${
                        c.configured ? "pcs-bg-pink text-white" : "bg-white"
                      }`}
                    >
                      {c.mode}
                    </span>
                  </div>
                  <p className="mt-3 text-sm text-gray-700">
                    {c.configured
                      ? "Live mode — pulling from real affiliate API."
                      : "Stub mode — add API keys to backend/.env to switch to live."}
                  </p>
                  {c.configured && c.environment && (
                    <div className="mt-3 inline-block brut pcs-bg-orange px-2 py-0.5 font-mono uppercase text-[10px] tracking-widest">
                      env: {c.environment}
                      {c.marketplace ? ` · ${c.marketplace}` : ""}
                    </div>
                  )}
                  <div className="mt-3 font-mono text-[11px] uppercase tracking-widest opacity-70">
                    Connector key: {c.name}
                  </div>
                </div>
              ))}
            </div>

            <ImportPanel
              vendor="ebay"
              vendorLabel="eBay"
              configured={stats.connectors.find((c) => c.name === "ebay")?.configured}
              maxLimit={50}
              helpText="Searches eBay Browse API with your EPN campaign attached. Each result is upserted as a Product with the affiliate URL eBay returns. Clicks via /api/go/<slug> get attributed to your campaign."
            />
            <ImportPanel
              vendor="amazon"
              vendorLabel="Amazon"
              configured={stats.connectors.find((c) => c.name === "amazon")?.configured}
              maxLimit={10}
              helpText="Searches Amazon via PA-API 5.0 with your Associate Tag attached. Each result is upserted as a Product with the affiliate-tagged DetailPageURL Amazon returns. PA-API caps at 10 items per call and 1 request/sec by default."
            />

            <div className="md:col-span-3 brut pcs-bg-cream p-5">
              <div className="font-mono uppercase text-[11px] tracking-widest opacity-70 mb-2">
                How to plug in real keys
              </div>
              <ul className="text-sm space-y-1 list-disc pl-5">
                <li>
                  <strong>eBay Partner Network:</strong> set{" "}
                  <code className="font-mono">EBAY_APP_ID</code>,{" "}
                  <code className="font-mono">EBAY_CERT_ID</code>, and{" "}
                  <code className="font-mono">EBAY_CAMPAIGN_ID</code> in backend/.env.
                </li>
                <li>
                  <strong>Amazon PA-API:</strong> set{" "}
                  <code className="font-mono">AMAZON_ACCESS_KEY</code>,{" "}
                  <code className="font-mono">AMAZON_SECRET_KEY</code>, and{" "}
                  <code className="font-mono">AMAZON_ASSOCIATE_TAG</code>{" "}
                  (e.g. <code className="font-mono">partycostumeshop-20</code>).
                </li>
              </ul>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value, accent }) {
  const cls =
    accent === "pink"
      ? "pcs-bg-pink text-white"
      : accent === "blue"
        ? "pcs-bg-blue text-white"
        : "pcs-bg-orange text-black";
  return (
    <div className={`brut p-5 ${cls}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="font-mono uppercase text-[11px] tracking-widest opacity-80">
        {label}
      </div>
      <div className="font-display text-5xl mt-1 leading-none">{value}</div>
    </div>
  );
}

function ImportPanel({ vendor, vendorLabel, configured, maxLimit = 50, helpText }) {
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(Math.min(20, maxLimit));
  const [categories, setCategories] = useState([]);
  const [cats, setCats] = useState([]);
  const [featured, setFeatured] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [showNewCat, setShowNewCat] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [creatingCat, setCreatingCat] = useState(false);

  const loadCategories = () => api.get("/categories").then((r) => setCategories(r.data));

  useEffect(() => {
    loadCategories();
  }, []);

  const toggleCat = (slug) => {
    setCats((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    );
  };

  const slugifyClient = (s) =>
    (s || "")
      .toLowerCase()
      .trim()
      .replace(/['']/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const createCategory = async (e) => {
    e.preventDefault();
    const name = newCatName.trim();
    if (!name) return;
    setCreatingCat(true);
    try {
      const { data } = await api.post("/categories", {
        name,
        slug: slugifyClient(name),
        description: "",
        image_url: "",
        sort_order: 100,
      });
      await loadCategories();
      setCats((prev) => (prev.includes(data.slug) ? prev : [...prev, data.slug]));
      setNewCatName("");
      setShowNewCat(false);
      toast.success(`Category "${data.name}" created and selected`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Could not create category";
      toast.error(msg);
    } finally {
      setCreatingCat(false);
    }
  };

  const run = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post(`/admin/import/${vendor}`, {
        query: query.trim(),
        limit: Math.min(Number(limit) || maxLimit, maxLimit),
        category_slugs: cats,
        featured,
      });
      setResult(data);
      const envLabel = data.environment ? ` (${data.environment})` : "";
      toast.success(
        `Imported ${data.created} new + ${data.updated} updated from ${vendorLabel}${envLabel}`
      );
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(typeof msg === "string" ? msg : "Import failed");
      setResult({ error: typeof msg === "string" ? msg : "Import failed" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="brut bg-white p-5" data-testid={`${vendor}-import-panel`}>
      <div className="flex items-center gap-2 mb-3">
        <Download className="w-4 h-4" strokeWidth={2.5} />
        <div className="font-display text-2xl tracking-tight">
          Import from {vendorLabel}
        </div>
        <span
          className={`brut px-2 py-0.5 font-mono uppercase text-[10px] tracking-widest ml-auto ${
            configured ? "pcs-bg-pink text-white" : "bg-white"
          }`}
        >
          {configured ? "ready" : "set keys first"}
        </span>
      </div>
      <p className="text-sm text-gray-700 mb-4">{helpText}</p>

      <form onSubmit={run} className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="font-mono uppercase text-[11px] tracking-widest block mb-1.5">
            Search query
          </label>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="vampire costume, halloween wig, party balloons..."
            data-testid={`${vendor}-import-query`}
            disabled={!configured}
            className="w-full brut bg-white px-3 py-2 focus:outline-none disabled:opacity-60"
          />
        </div>
        <div>
          <label className="font-mono uppercase text-[11px] tracking-widest block mb-1.5">
            Limit (max {maxLimit})
          </label>
          <input
            type="number"
            min="1"
            max={maxLimit}
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            data-testid={`${vendor}-import-limit`}
            disabled={!configured}
            className="w-full brut bg-white px-3 py-2 focus:outline-none disabled:opacity-60"
          />
        </div>

        <div className="md:col-span-2">
          <label className="font-mono uppercase text-[11px] tracking-widest block mb-1.5">
            Assign to categories
          </label>
          <div className="flex flex-wrap gap-2 items-center">
            {categories.map((c) => (
              <button
                type="button"
                key={c.slug}
                onClick={() => toggleCat(c.slug)}
                disabled={!configured}
                data-testid={`${vendor}-import-cat-${c.slug}`}
                title={c.name}
                className={`brut px-2 py-1 font-mono uppercase text-[10px] tracking-widest brut-press disabled:opacity-60 ${
                  cats.includes(c.slug) ? "pcs-bg-blue text-white" : "bg-white"
                }`}
              >
                {c.slug}
              </button>
            ))}
            {!showNewCat && (
              <button
                type="button"
                onClick={() => setShowNewCat(true)}
                disabled={!configured}
                data-testid={`${vendor}-import-newcat-toggle`}
                className="brut pcs-bg-orange px-2 py-1 font-mono uppercase text-[10px] tracking-widest brut-press disabled:opacity-60"
              >
                + new category
              </button>
            )}
          </div>

          {showNewCat && (
            <div
              className="mt-3 brut bg-white p-3 flex flex-col sm:flex-row gap-2 sm:items-center"
              data-testid={`${vendor}-import-newcat-form`}
            >
              <input
                value={newCatName}
                onChange={(e) => setNewCatName(e.target.value)}
                placeholder="Category name (e.g. Wigs & Hair)"
                data-testid={`${vendor}-import-newcat-input`}
                className="flex-1 brut bg-white px-3 py-2 focus:outline-none"
                autoFocus
              />
              {newCatName.trim() && (
                <span className="font-mono text-[11px] uppercase tracking-widest opacity-70">
                  → /category/{slugifyClient(newCatName)}
                </span>
              )}
              <button
                type="button"
                onClick={createCategory}
                disabled={creatingCat || !newCatName.trim()}
                data-testid={`${vendor}-import-newcat-create`}
                className="brut pcs-bg-pink text-white px-3 py-2 font-bold uppercase text-xs tracking-wide brut-press disabled:opacity-60"
              >
                {creatingCat ? "Creating..." : "Create"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowNewCat(false);
                  setNewCatName("");
                }}
                disabled={creatingCat}
                data-testid={`${vendor}-import-newcat-cancel`}
                className="brut bg-white px-3 py-2 font-mono uppercase text-[11px] tracking-widest brut-press disabled:opacity-60"
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        <label className="md:col-span-2 inline-flex items-center gap-2 font-mono uppercase text-[11px] tracking-widest cursor-pointer">
          <input
            type="checkbox"
            checked={featured}
            onChange={(e) => setFeatured(e.target.checked)}
            disabled={!configured}
            data-testid={`${vendor}-import-featured`}
            className="w-4 h-4 border-2 border-black"
          />
          Mark imported items as featured
        </label>

        <div className="md:col-span-2 flex items-center gap-3 flex-wrap">
          <button
            type="submit"
            disabled={!configured || busy || !query.trim()}
            data-testid={`${vendor}-import-submit`}
            className="brut pcs-bg-pink text-white px-5 py-2 font-bold uppercase tracking-wide text-sm brut-press disabled:opacity-60"
          >
            {busy ? "Importing..." : "Run import"}
          </button>
          {result && !result.error && (
            <div
              className="font-mono text-[11px] uppercase tracking-widest"
              data-testid={`${vendor}-import-result`}
            >
              {result.environment ? `${result.environment} · ` : ""}found {result.found} · created {result.created} · updated{" "}
              {result.updated} · skipped {result.skipped}
            </div>
          )}
          {result?.error && (
            <div
              className="font-mono text-[11px] pcs-text-pink"
              data-testid={`${vendor}-import-error`}
            >
              {result.error}
            </div>
          )}
        </div>
      </form>
    </div>
  );
}


function CategoryRow({ cat, onSaved, onDeleted }) {
  const [draft, setDraft] = useState(cat);
  const [busy, setBusy] = useState(false);

  // Reset draft if parent gives us a different category snapshot
  useEffect(() => {
    setDraft(cat);
  }, [cat]);

  const dirty =
    draft.name !== cat.name ||
    draft.description !== cat.description ||
    draft.image_url !== cat.image_url ||
    Number(draft.sort_order) !== Number(cat.sort_order);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/categories/${cat.slug}`, {
        name: draft.name,
        description: draft.description || "",
        image_url: draft.image_url || "",
        sort_order: Number(draft.sort_order) || 100,
      });
      toast.success(`Saved "${data.name}"`);
      onSaved(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!window.confirm(`Delete category "${cat.name}"? Products keep their tag but the public page disappears.`)) return;
    setBusy(true);
    try {
      await api.delete(`/categories/${cat.slug}`);
      toast.success(`Deleted "${cat.name}"`);
      onDeleted(cat.slug);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Delete failed");
      setBusy(false);
    }
  };

  return (
    <div
      className="brut bg-white p-4 grid md:grid-cols-12 gap-3 items-start"
      data-testid={`admin-cat-row-${cat.slug}`}
    >
      <div className="md:col-span-2">
        <div className="brut bg-white aspect-square overflow-hidden">
          {draft.image_url ? (
            <img
              src={draft.image_url}
              alt={draft.name || ""}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center font-mono text-[10px] uppercase tracking-widest opacity-40 text-center p-2">
              No image
            </div>
          )}
        </div>
        <div className="mt-1 font-mono uppercase text-[10px] tracking-widest opacity-70 truncate">
          /category/{cat.slug}
        </div>
      </div>

      <div className="md:col-span-10 grid md:grid-cols-12 gap-3">
        <div className="md:col-span-5">
          <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
            Name
          </label>
          <input
            value={draft.name || ""}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            data-testid={`admin-cat-name-${cat.slug}`}
            className="w-full brut bg-white px-3 py-2 focus:outline-none"
          />
        </div>
        <div className="md:col-span-5">
          <ImageUploader
            label="Image"
            value={draft.image_url || ""}
            onChange={(url) => setDraft({ ...draft, image_url: url })}
            testId={`admin-cat-image-${cat.slug}`}
            aspect="aspect-square"
          />
        </div>
        <div className="md:col-span-2">
          <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
            Sort order
          </label>
          <input
            type="number"
            value={draft.sort_order ?? 100}
            onChange={(e) =>
              setDraft({ ...draft, sort_order: Number(e.target.value) })
            }
            data-testid={`admin-cat-sort-${cat.slug}`}
            className="w-full brut bg-white px-3 py-2 focus:outline-none"
          />
        </div>
        <div className="md:col-span-12">
          <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
            Description{" "}
            <span className="opacity-60 normal-case tracking-normal">
              (Markdown supported — **bold**, *italic*, [links](url), lists)
            </span>
          </label>
          <textarea
            value={draft.description || ""}
            onChange={(e) =>
              setDraft({ ...draft, description: e.target.value })
            }
            rows={5}
            placeholder={"Short copy shown on the category header.\n\nUse **bold**, *italic*, and [links](https://...) — line breaks become paragraphs."}
            data-testid={`admin-cat-desc-${cat.slug}`}
            className="w-full brut bg-white px-3 py-2 focus:outline-none resize-y font-mono text-xs"
          />
        </div>
        <div className="md:col-span-12 flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={save}
            disabled={busy || !dirty}
            data-testid={`admin-cat-save-${cat.slug}`}
            className="brut pcs-bg-pink text-white px-3 py-2 font-bold uppercase text-[11px] tracking-wide brut-press disabled:opacity-50"
          >
            {busy ? "Saving..." : dirty ? "Save changes" : "Saved"}
          </button>
          <Link
            to={`/category/${cat.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="brut bg-white px-3 py-2 font-mono uppercase text-[11px] tracking-widest brut-press"
          >
            View live →
          </Link>
          <button
            type="button"
            onClick={del}
            disabled={busy}
            data-testid={`admin-cat-delete-${cat.slug}`}
            className="ml-auto brut bg-white px-3 py-2 font-mono uppercase text-[11px] tracking-widest brut-press disabled:opacity-50"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function NewCategoryForm({ onCreated }) {
  const [name, setName] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [description, setDescription] = useState("");
  const [sortOrder, setSortOrder] = useState(100);
  const [busy, setBusy] = useState(false);

  const slugifyClient = (s) =>
    (s || "")
      .toLowerCase()
      .trim()
      .replace(/['']/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/categories", {
        name: name.trim(),
        slug: slugifyClient(name),
        description: description.trim(),
        image_url: imageUrl.trim(),
        sort_order: Number(sortOrder) || 100,
      });
      toast.success(`Created "${data.name}"`);
      setName("");
      setImageUrl("");
      setDescription("");
      setSortOrder(100);
      onCreated(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Create failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="admin-newcat-form"
      className="brut bg-white p-4 grid md:grid-cols-12 gap-3 items-end"
    >
      <div className="md:col-span-12 flex items-center gap-2">
        <div className="font-display text-xl tracking-tight">
          + New category
        </div>
        {name.trim() && (
          <span className="font-mono text-[11px] uppercase tracking-widest opacity-70">
            → /category/{slugifyClient(name)}
          </span>
        )}
      </div>
      <div className="md:col-span-4">
        <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
          Name *
        </label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Wigs & Hair"
          required
          data-testid="admin-newcat-name"
          className="w-full brut bg-white px-3 py-2 focus:outline-none"
        />
      </div>
      <div className="md:col-span-5">
        <ImageUploader
          label="Image"
          value={imageUrl}
          onChange={(url) => setImageUrl(url)}
          testId="admin-newcat-image"
          aspect="aspect-square"
        />
      </div>
      <div className="md:col-span-2">
        <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
          Sort order
        </label>
        <input
          type="number"
          value={sortOrder}
          onChange={(e) => setSortOrder(Number(e.target.value))}
          data-testid="admin-newcat-sort"
          className="w-full brut bg-white px-3 py-2 focus:outline-none"
        />
      </div>
      <div className="md:col-span-1 flex">
        <button
          type="submit"
          disabled={busy || !name.trim()}
          data-testid="admin-newcat-submit"
          className="w-full brut pcs-bg-pink text-white px-3 py-2 font-bold uppercase text-[11px] tracking-wide brut-press disabled:opacity-60"
        >
          {busy ? "..." : "Add"}
        </button>
      </div>
      <div className="md:col-span-12">
        <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
          Description{" "}
          <span className="opacity-60 normal-case tracking-normal">
            (Markdown supported)
          </span>
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          placeholder={"Short copy shown on the category header.\n\nUse **bold**, *italic*, and [links](https://...)."}
          data-testid="admin-newcat-desc"
          className="w-full brut bg-white px-3 py-2 focus:outline-none resize-y font-mono text-xs"
        />
      </div>
    </form>
  );
}

function CategoriesPanel({ initialSlug }) {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    return api.get("/categories").then((r) => {
      setCats(r.data);
      setLoading(false);
    });
  };

  useEffect(() => {
    load();
  }, []);

  // When deep-linked from a public Category page, scroll to & highlight
  // the matching row once it's rendered.
  useEffect(() => {
    if (!initialSlug || loading) return;
    const el = document.querySelector(`[data-testid="admin-cat-row-${initialSlug}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("ring-4", "ring-pink-400");
    const t = setTimeout(() => el.classList.remove("ring-4", "ring-pink-400"), 2500);
    return () => clearTimeout(t);
  }, [initialSlug, loading, cats]);

  const sorted = [...cats].sort(
    (a, b) =>
      (a.sort_order ?? 100) - (b.sort_order ?? 100) ||
      a.name.localeCompare(b.name)
  );

  return (
    <div className="space-y-4" data-testid="admin-categories-panel">
      <NewCategoryForm
        onCreated={(c) => setCats((prev) => [...prev, c])}
      />

      <div className="brut pcs-bg-cream px-4 py-3 font-mono uppercase text-[11px] tracking-widest flex items-center justify-between">
        <span>{cats.length} categories · ordered by sort_order ascending</span>
        <button
          type="button"
          onClick={load}
          className="brut bg-white px-2 py-1 text-[10px] brut-press"
          data-testid="admin-cats-refresh"
        >
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="font-mono uppercase text-xs tracking-widest opacity-70">
          Loading...
        </div>
      ) : (
        <div className="space-y-3">
          {sorted.map((c) => (
            <CategoryRow
              key={c.slug}
              cat={c}
              onSaved={(updated) =>
                setCats((prev) =>
                  prev.map((x) => (x.slug === updated.slug ? updated : x))
                )
              }
              onDeleted={(slug) =>
                setCats((prev) => prev.filter((x) => x.slug !== slug))
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}


function detectVendorFromUrl(url) {
  if (!url) return { vendor: "", note: "" };
  let host = "";
  try {
    host = new URL(url).hostname.toLowerCase();
  } catch {
    return { vendor: "", note: "Not a valid URL yet" };
  }
  if (host.includes("amazon.")) {
    // Try to extract Associates tag for confirmation
    const tagMatch = url.match(/[?&]tag=([^&#]+)/i);
    if (tagMatch) {
      return {
        vendor: "amazon",
        note: `Amazon affiliate link detected · tag: ${decodeURIComponent(tagMatch[1])} ✓`,
        good: true,
      };
    }
    return {
      vendor: "amazon",
      note: "Amazon URL detected, but no ?tag= parameter — clicks won't earn commission. Use SiteStripe → Text or Image link.",
      warn: true,
    };
  }
  if (host.includes("ebay.")) {
    return { vendor: "ebay", note: "eBay URL detected", good: true };
  }
  return { vendor: "other", note: "Unknown vendor — set manually below" };
}

function extractAmazonAsin(url) {
  if (!url) return "";
  const m =
    url.match(/\/dp\/([A-Z0-9]{10})/i) ||
    url.match(/\/gp\/product\/([A-Z0-9]{10})/i) ||
    url.match(/\/ASIN\/([A-Z0-9]{10})/i);
  return m ? m[1].toUpperCase() : "";
}

function ManualAddProductPanel({ onCreated }) {
  const [title, setTitle] = useState("");
  const [vendor, setVendor] = useState("amazon");
  const [affiliateUrl, setAffiliateUrl] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");
  const [categories, setCategories] = useState([]);
  const [cats, setCats] = useState([]);
  const [featured, setFeatured] = useState(false);
  const [busy, setBusy] = useState(false);
  const [scraping, setScraping] = useState(false);

  const loadCategories = () => api.get("/categories").then((r) => setCategories(r.data));

  useEffect(() => {
    loadCategories();
  }, []);

  const toggleCat = (slug) => {
    setCats((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    );
  };

  // Auto-set vendor when URL changes
  const detected = detectVendorFromUrl(affiliateUrl);
  useEffect(() => {
    if (detected.vendor && detected.vendor !== "other") {
      setVendor(detected.vendor);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [affiliateUrl]);

  const slugifyClient = (s) =>
    (s || "")
      .toLowerCase()
      .trim()
      .replace(/['']/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const reset = () => {
    setTitle("");
    setAffiliateUrl("");
    setImageUrl("");
    setPrice("");
    setDescription("");
    setCats([]);
    setFeatured(false);
  };

  const quickFillFromAmazon = async () => {
    const url = affiliateUrl.trim();
    if (!url) {
      toast.error("Paste the Amazon SiteStripe URL first");
      return;
    }
    if (!/amazon\.|amzn\./.test(url)) {
      toast.error("URL doesn't look like Amazon");
      return;
    }
    setScraping(true);
    try {
      const { data } = await api.post("/admin/scrape/amazon", { url });
      if (data.title && !title) setTitle(data.title);
      if (data.image_url && !imageUrl) setImageUrl(data.image_url);
      if (data.price && !price) setPrice(String(data.price));
      const got = [
        data.title && "title",
        data.image_url && "image",
        data.price && "price",
      ].filter(Boolean);
      if (got.length === 0) {
        toast.warning(
          (data.notes && data.notes[0]) ||
            "Couldn't extract anything — Amazon may be blocking. Fill manually."
        );
      } else {
        toast.success(`Quick-filled: ${got.join(", ")}${data.notes?.length ? " (some fields manual)" : ""}`);
        if (data.notes?.length) {
          data.notes.forEach((n) => toast.message(n));
        }
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Quick-fill failed");
    } finally {
      setScraping(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !affiliateUrl.trim()) return;
    setBusy(true);

    // Build slug — for Amazon add ASIN suffix for uniqueness
    const titleSlug = slugifyClient(title);
    const asin = vendor === "amazon" ? extractAmazonAsin(affiliateUrl) : "";
    const slug = asin ? `${titleSlug}-${asin.toLowerCase()}` : titleSlug;

    try {
      const { data } = await api.post("/products", {
        slug,
        title: title.trim(),
        description: description.trim(),
        image_url: imageUrl.trim(),
        price: Number(price) || 0,
        currency: "USD",
        vendor: vendor || "other",
        category_slugs: cats,
        affiliate_url: affiliateUrl.trim(),
        tags: asin ? [asin] : [],
        featured,
      });
      toast.success(`Added "${data.title}"`);
      onCreated(data);
      reset();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not add product");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="brut bg-white p-5 grid md:grid-cols-12 gap-4"
      data-testid="manual-add-product-form"
    >
      <div className="md:col-span-12 flex items-center gap-2">
        <Package className="w-4 h-4" strokeWidth={2.5} />
        <div className="font-display text-2xl tracking-tight">Add a product manually</div>
        <span className="ml-auto font-mono text-[10px] uppercase tracking-widest opacity-60">
          For Amazon SiteStripe links & other vendors
        </span>
      </div>

      <div className="md:col-span-12">
        <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
          Affiliate URL * (paste your SiteStripe link)
        </label>
        <div className="flex gap-2">
          <input
            value={affiliateUrl}
            onChange={(e) => setAffiliateUrl(e.target.value)}
            placeholder="https://www.amazon.com/dp/B0XXXXXXXX?tag=partycostumeshop-20&..."
            required
            data-testid="manual-add-url"
            className="flex-1 brut bg-white px-3 py-2 focus:outline-none font-mono text-xs"
          />
          {affiliateUrl.trim() && /amazon\.|amzn\./.test(affiliateUrl) && (
            <button
              type="button"
              onClick={quickFillFromAmazon}
              disabled={scraping || !affiliateUrl.trim()}
              data-testid="manual-add-quick-fill"
              className="brut pcs-bg-blue text-white px-3 py-2 font-bold uppercase text-xs tracking-wide brut-press disabled:opacity-50 whitespace-nowrap"
            >
              {scraping ? "Fetching..." : "Quick fill"}
            </button>
          )}
        </div>
        {detected.note && (
          <div
            className={`mt-1 font-mono uppercase text-[10px] tracking-widest ${
              detected.good ? "pcs-text-pink" : detected.warn ? "text-orange-700" : "opacity-70"
            }`}
            data-testid="manual-add-url-status"
          >
            {detected.note}
          </div>
        )}
      </div>

      <div className="md:col-span-8">
        <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
          Title *
        </label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Vampire Cape — Black Velvet"
          required
          data-testid="manual-add-title"
          className="w-full brut bg-white px-3 py-2 focus:outline-none"
        />
      </div>

      <div className="md:col-span-2">
        <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
          Vendor
        </label>
        <select
          value={vendor}
          onChange={(e) => setVendor(e.target.value)}
          data-testid="manual-add-vendor"
          className="w-full brut bg-white px-3 py-2 focus:outline-none"
        >
          <option value="amazon">Amazon</option>
          <option value="ebay">eBay</option>
          <option value="other">Other</option>
        </select>
      </div>

      <div className="md:col-span-2">
        <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
          Price ($)
        </label>
        <input
          type="number"
          step="0.01"
          min="0"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          placeholder="29.99"
          data-testid="manual-add-price"
          className="w-full brut bg-white px-3 py-2 focus:outline-none"
        />
      </div>

      <div className="md:col-span-12">
        <ImageUploader
          label='Image (right-click an Amazon image → "Copy image address" or drag a file)'
          value={imageUrl}
          onChange={(url) => setImageUrl(url)}
          testId="manual-add-image"
          aspect="aspect-square max-w-xs"
        />
      </div>

      <div className="md:col-span-12">
        <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
          Description{" "}
          <span className="opacity-60 normal-case tracking-normal">
            (Markdown — **bold**, *italic*, lists, [links](url), tables)
          </span>
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={6}
          placeholder={"Editorial pitch — appears on the product detail page.\n\n## Why we love it\nGreat fit, durable fabric, ships fast.\n\n### Pros\n- Lightweight\n- Easy to clean\n\n### Cons\n- Runs slightly small — size up.\n\n| Size | Chest |\n|------|-------|\n| S    | 36\"   |\n| M    | 40\"   |"}
          data-testid="manual-add-description"
          className="w-full brut bg-white px-3 py-2 focus:outline-none resize-y font-mono text-xs"
        />
      </div>

      <div className="md:col-span-12">
        <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
          Assign to categories
        </label>
        <div className="flex flex-wrap gap-2 items-center">
          {categories.map((c) => (
            <button
              type="button"
              key={c.slug}
              onClick={() => toggleCat(c.slug)}
              data-testid={`manual-add-cat-${c.slug}`}
              className={`brut px-2 py-1 font-mono uppercase text-[10px] tracking-widest brut-press ${
                cats.includes(c.slug) ? "pcs-bg-blue text-white" : "bg-white"
              }`}
            >
              {c.slug}
            </button>
          ))}
        </div>
      </div>

      <label className="md:col-span-6 inline-flex items-center gap-2 font-mono uppercase text-[11px] tracking-widest cursor-pointer">
        <input
          type="checkbox"
          checked={featured}
          onChange={(e) => setFeatured(e.target.checked)}
          data-testid="manual-add-featured"
          className="w-4 h-4 border-2 border-black"
        />
        Featured (appears on homepage)
      </label>

      <div className="md:col-span-6 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={reset}
          disabled={busy}
          className="brut bg-white px-3 py-2 font-mono uppercase text-[11px] tracking-widest brut-press disabled:opacity-60"
        >
          Clear
        </button>
        <button
          type="submit"
          disabled={busy || !title.trim() || !affiliateUrl.trim()}
          data-testid="manual-add-submit"
          className="brut pcs-bg-pink text-white px-5 py-2 font-bold uppercase tracking-wide text-sm brut-press disabled:opacity-60"
        >
          {busy ? "Adding..." : "Add product"}
        </button>
      </div>
    </form>
  );
}
